import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { anchoredOffCentre, check } from '../skillUsage';

export const squashAndStretchSkill: CodeoSkill = {
    name: SKILL_NAMES.squashAndStretch,
    kind: 'codeo',
    description:
        'Let a soft object change shape with its motion — stretched along its path when it moves ' +
        'fast, squashed flat against the floor on impact, round at the top of each arc — so it ' +
        'reads as flexible and alive instead of hard and lifeless.',
    checkLooksFor:
        'scaleX and scaleY tweened apart on an element whose transform origin is its contact edge (50% 100%), not its centre',
    documentUsesSkill: check(
        (script, _markup, html) =>
            /scaleX/.test(script) && /scaleY/.test(script) && anchoredOffCentre(html),
    ),
    reviewNote:
        'A round object drawn as a tall tilted ellipse in the air, or a wide flat one on the ' +
        'floor, is this technique working: it is not a distorted or squashed-by-mistake shape.',
    instructions: `# Squash and Stretch

A soft object changes shape as it moves. It **stretches along its direction of travel when it moves fast**, **squashes flat against the floor when it hits**, and is **round at the top of each arc**, where it is slowest. The same bounce on a rigid circle reads as hard and lifeless. The shape change is what makes it flexible, and the way it meets the floor is what makes it land.

## The rules

- **Stretched only in the air, squashed only on the floor.** The stretch builds over the last 60% of each fall, as the speed builds. At contact the shape turns from stretch to squash in one frame, with no round, touching pose in between. Off the floor it springs back into a stretch, and it is round again a third of the way up.
- **It bounces off a visible surface.** A contact with nothing under it looks like the element hitting thin air. Draw what it lands on: a floor line, the top edge of a shape or panel, or the frame's bottom edge. The surface sits exactly where the element's bottom is at contact, runs under every contact along the path, and is strong enough to read at a glance; a faint hairline does not count. A contact shadow does not count as the surface on its own. If you add one, it travels with the x wrapper so it stays under the element.
- **The contact edge never moves.** Scale about the bottom (\`transform-origin: 50% 100%\`), so a squash widens along the floor with the bottom exactly on it, never sunk below and never floating above. Position the element by that bottom edge: the floor is where its bottom rests.
- **The squash holds briefly**, about 0.05s, while the element is on the floor (0.08s of contact in all), then it springs off.
- **The amount follows the impact, decaying bounce by bounce.**

| Contact | Fall stretch (before) | Squash (on the floor) | Rebound stretch (after) |
|---|---|---|---|
| 1st | 0.62 × 1.38 | 1.5 × 0.5 | 0.81 × 1.19 |
| 2nd | 0.78 × 1.22 | 1.25 × 0.75 | 0.875 × 1.125 |
| 3rd | 0.85 × 1.15 | 1.1 × 0.9 | 0.94 × 1.06 |
| lower hops | none | none | none |

  Widths and heights always add to 2 (1.5 × 0.5, 0.78 × 1.22): the object keeps its bulk without going thin. Hops under about a third of the first one's height are too slow to deform.
- **The stretch leans along the path.** Its long axis follows the direction of travel (moving right, the top trails back to the left), so the lean is the path's angle from vertical near the contact: the angle whose tangent is the sideways speed over the falling speed (for a \`power2.in\` fall, the falling speed at contact is twice the fall height over the fall time). At the example's speeds that is about 15–20° on the fall and about 10° the other way on the rebound; a slow sideways drift leans only a degree or two, and a straight drop stays upright. Flat (0°) while squashed.
- **The path is an arc**: linear x, eased y (\`${SKILL_NAMES.arcPathMotion}\`). Falls ease in onto the floor, rises ease out into the apex.

## Markup

Three layers: x on the outer wrapper, y on the inner one, and the shape on the ball itself. The floor line's top edge is exactly where the ball's bottom rests.

\`\`\`html
<div id="sq-stage" style="position: absolute; inset: 0; display: grid; place-items: center; overflow: hidden;">
  <div id="sq-floor" data-l5-absolute style="position: absolute; left: 0; right: 0; top: calc(50% + 100px); height: 8px; background: var(--l5-color-0, #101820);"></div>
  <div id="sq-x" style="grid-area: 1 / 1;">
    <div id="sq-y">
      <div id="sq-ball" style="width: 200px; height: 200px; border-radius: 50%; background: var(--l5-color-2, #ff5a36);"></div>
    </div>
  </div>
</div>
\`\`\`

## Timeline code

The ball falls in from the left and comes to rest on the frame's centre: three deforming contacts, then one small rigid hop.

\`\`\`js
const tl = gsap.timeline({ paused: true });

const LAND = 0.6;        // first contact: the word it lands on
const CONTACT = 0.08;    // time the ball's bottom stays on the floor
const ENTRY = { height: 330, fall: 0.34 };
// Contact to contact.
const HOPS = [
    { height: 250, duration: 0.5 },
    { height: 195, duration: 0.42 },
    { height: 110, duration: 0.26 },
];
// One row per contact: stretch on the way down, squash on the floor, stretch on the way up.
const DEFORM = [
    { fall: 0.38, squash: 0.5, rebound: 0.19 },
    { fall: 0.22, squash: 0.25, rebound: 0.125 },
    { fall: 0.15, squash: 0.1, rebound: 0.06 },
    { fall: 0, squash: 0, rebound: 0 },
];
const SPEED_X = 700;     // px/s, constant
const LEAN = 15;         // degrees the stretch tilts toward the direction of travel
const TURN = 0.02;       // stretch to squash, centred on the contact

const contacts = [LAND];
HOPS.forEach((hop) => contacts.push(contacts[contacts.length - 1] + hop.duration));
const REST = contacts[contacts.length - 1];
const START = LAND - ENTRY.fall;

gsap.set('#sq-ball', { transformOrigin: '50% 100%' });
gsap.set('#sq-y', { y: -ENTRY.height });
gsap.set('#sq-x', { x: -SPEED_X * (REST - START) });

// x: one linear tween for the whole path.
tl.to('#sq-x', { x: 0, duration: REST - START, ease: 'none' }, START);

// y: falls ease in onto the floor, rises ease out into the apex.
tl.to('#sq-y', { y: 0, duration: ENTRY.fall, ease: 'power2.in' }, START);
HOPS.forEach((hop, i) => {
    const leave = contacts[i] + CONTACT;
    const half = (hop.duration - CONTACT) / 2;
    tl.to('#sq-y', { y: -hop.height, duration: half, ease: 'power2.out' }, leave);
    tl.to('#sq-y', { y: 0, duration: half, ease: 'power2.in' }, leave + half);
});

// The shape, contact by contact.
DEFORM.forEach((d, i) => {
    if (d.squash === 0) return;
    const hit = contacts[i];
    const fall = i === 0 ? ENTRY.fall : (HOPS[i - 1].duration - CONTACT) / 2;
    // Stretch as it speeds up, leaning along the path.
    tl.to('#sq-ball', { scaleX: 1 - d.fall, scaleY: 1 + d.fall, rotation: -LEAN, duration: fall * 0.6 - TURN, ease: 'power2.in' }, hit - fall * 0.6);
    // Squash flat on the floor.
    tl.to('#sq-ball', { scaleX: 1 + d.squash, scaleY: 1 - d.squash, rotation: 0, duration: 2 * TURN, ease: 'none' }, hit - TURN);
    // Hold it, then spring off the floor into the rebound stretch, and round again.
    tl.to('#sq-ball', { scaleX: 1 - d.rebound, scaleY: 1 + d.rebound, rotation: LEAN * 0.7, duration: 0.06, ease: 'power1.inOut' }, hit + 0.05);
    tl.to('#sq-ball', { scaleX: 1, scaleY: 1, rotation: 0, duration: 0.14, ease: 'sine.out' }, hit + 0.11);
});

tl.set({}, {}, REST + 0.6);
window.__timelines = { main: tl };
\`\`\`

## Adapt

- **A straight drop.** Drop the x wrapper's tween and set \`LEAN\` to 0: the ball stretches straight up and down, squashes flat. Without the sideways offset, the entry height alone has to keep the ball out of view before it falls: raise it until the ball starts wholly above the frame (more than the distance from the frame's top to the ball's resting top, plus its own height), and lengthen the fall with the square root of the height (twice the height is about 1.4× the time).
- **Softness is the amount.** The table is a rubber ball. A firmer object uses about half the numbers; a water balloon pushes the first squash toward 1.8 × 0.2. For a jump-scale exaggeration on the key moment, see \`${SKILL_NAMES.exaggeratedPoses}\`.
- **Moving left.** Flip the sign of \`LEAN\`: the top always trails behind the travel.
- **Landing a word.** A word that slams down onto its baseline can take one contact's worth: squash to about 1.15 × 0.85 on the baseline and spring back, without the in-air stretch, so it is never deformed while it has to be read.
- **Weight.** Fewer, lower bounces and a faster fall make it heavier: \`${SKILL_NAMES.massAndWeight}\`.

## Pitfalls

- **Never slow it to fill a scene.** The hop times are the gravity, and the deformation timing only works at that speed. A hop's time grows with the square root of its height (half as high is about 0.7× as long), so keep the example's times for its heights; hops several times longer than these turn a rubber ball into a balloon. However long the scene, the bounce takes its own time and the ball then rests.
- **Start it moving.** A ball visible before it falls hangs in mid-air. Its starting offsets have to carry it past the frame edge from its resting place, which is why the example derives the x offset from the speed and the time in flight. If the first contact comes later in the scene, start the fall later, not from a visible position.
- **Never squash in the air or stretch on the floor.** A ball that flattens before it touches, or touches while still tall, reads as a glitch.
- **The drawn edge touches, not the box.** The element's box has to end where its drawing ends. An SVG whose viewBox leaves room below the shape, or an image with a transparent margin, rests that far above the floor and never touches it, and its scale pivots below the drawing. Crop the viewBox to the shape, or offset the element by the margin, so the drawn bottom is the box's bottom, then look at a still on a contact frame.
- **Scale about the centre and the ball sinks into the floor**, its squashed bottom below the line, or floats above it when stretched. The origin is the contact edge.
- **Rigid things don't squash.** A phone, a building, a coin, a box with sharp corners: use \`${SKILL_NAMES.massAndWeight}\` alone. Squash belongs to soft, round, organic things.
- The squash has to be brief and the stretch has to be fast. Deformation held for longer than about a tenth of a second reads as a different shape, not as an impact.
- The ball is transform-tweened, so it must not carry a CSS \`transform\`. The wrappers carry the travel; only the shape tweens live on the ball.`,
};
